// frontend/src/pages/StreamersPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Eye, Tv, Users, Wifi, WifiOff } from 'lucide-react';
import Footer from '../components/Footer';
import { sanitizeTwitchUsername, sanitizeImageUrl } from '../utils/sanitize';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const CATEGORIES = [
  'All',
  'Just Chatting',
  'Fortnite',
  'Call of Duty',
  'Minecraft',
  'Valorant',
  'Apex Legends',
  'NBA 2K',
  'FIFA',
  'GTA V',
  'League of Legends',
  'Overwatch 2',
  'Elden Ring',
  'Resident Evil',
  'Dead by Daylight',
  'Phasmophobia',
  'Escape from Tarkov',
  'Fall Guys',
  'Rocket League',
  'Chess',
  'Art',
  'Music',
  'Sports',
];

const formatViewers = (n) => {
  if (!n) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
  return String(n);
};

const StreamersPage = () => {
  const [streamers,  setStreamers]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [category,   setCategory]   = useState('All');
  const [liveOnly,   setLiveOnly]   = useState(false);
  const [searchInput,setSearchInput]= useState('');

  const fetchStreamers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search)              params.set('search',    search);
      if (category !== 'All') params.set('category',  category);
      if (liveOnly)           params.set('live_only', 'true');
      const res  = await fetch(`${BACKEND_URL}/api/streamers?${params}`);
      const data = await res.json();
      setStreamers(data.streamers || []);
    } catch {
      setStreamers([]);
    } finally {
      setLoading(false);
    }
  }, [search, category, liveOnly]);

  useEffect(() => { fetchStreamers(); }, [fetchStreamers]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const liveCount = streamers.filter(s => s.is_live).length;

  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <section className="py-20 bg-black">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <h1 className="text-5xl lg:text-7xl font-black uppercase text-white leading-none mb-4">
            BGN Streamers
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl">
            Browse and discover Black gamers streaming across every category.
            {liveCount > 0 && <span className="text-red-400 ml-2">● {liveCount} live now</span>}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-0 z-10 bg-white border-b border-gray-200 py-4 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="flex flex-wrap items-center gap-3">

            {/* Search */}
            <div className="relative flex-1 min-w-48 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search streamers..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-200 text-sm focus:outline-none focus:border-black transition-colors"
              />
            </div>

            {/* Live toggle */}
            <button
              onClick={() => setLiveOnly(!liveOnly)}
              className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium border transition-colors duration-200 ${
                liveOnly ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-700 border-gray-200 hover:border-black'
              }`}
            >
              {liveOnly ? <Wifi size={14} /> : <WifiOff size={14} />}
              <span>Live Only</span>
            </button>

            {/* Category filter */}
            <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium uppercase tracking-wide border transition-colors duration-200 ${
                    category === cat
                      ? 'bg-black text-white border-black'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-black hover:text-black'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-gray-200 border-t-black rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-500 text-sm uppercase tracking-wider">Loading streamers...</p>
              </div>
            </div>
          ) : streamers.length === 0 ? (
            <div className="text-center py-24">
              <Users size={48} className="text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-black mb-2">No streamers found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          ) : (
            <>
              <p className="text-gray-500 text-sm mb-8">
                {streamers.length} streamer{streamers.length !== 1 ? 's' : ''}
                {liveCount > 0 && ` · ${liveCount} live`}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {streamers.map(streamer => (
                  <StreamerCard key={streamer.login} streamer={streamer} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};


// ── Streamer card ─────────────────────────────────────────────────────────────

const StreamerCard = ({ streamer }) => {
  const safeLogin     = sanitizeTwitchUsername(streamer.login);
  const safeAvatar    = sanitizeImageUrl(streamer.avatar_url);
  const safeThumbnail = sanitizeImageUrl(streamer.thumbnail_url);
  const profileUrl    = `/profile/${safeLogin}`;
  const twitchUrl     = safeLogin ? `https://twitch.tv/${safeLogin}` : null;

  if (!twitchUrl) return null;

  const cardClass = "bg-white border border-gray-200 overflow-hidden hover:shadow-md transition-all duration-200 group block";

  const inner = (
    <>
      {/* Thumbnail or avatar */}
      <div className="relative aspect-video bg-gray-100">
        {streamer.is_live && safeThumbnail ? (
          <img
            src={safeThumbnail}
            alt={`${streamer.display_name} stream`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : safeAvatar ? (
          <div className="w-full h-full flex items-center justify-center bg-gray-50">
            <img
              src={safeAvatar}
              alt={streamer.display_name}
              className="w-20 h-20 rounded-full border-2 border-gray-200"
            />
          </div>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-900">
            <Tv size={32} className="text-gray-600" />
          </div>
        )}

        {/* Live badge */}
        {streamer.is_live && (
          <div className="absolute top-2 left-2 flex items-center space-x-1 bg-red-600 text-white px-2 py-0.5">
            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
            <span className="text-xs font-medium uppercase tracking-wide">Live</span>
          </div>
        )}

        {/* Viewer count */}
        {streamer.is_live && streamer.viewer_count > 0 && (
          <div className="absolute bottom-2 right-2 bg-black/70 text-white px-2 py-0.5 text-xs flex items-center space-x-1">
            <Eye size={10} />
            <span>{formatViewers(streamer.viewer_count)}</span>
          </div>
        )}

        {/* BGN member badge */}
        {streamer.is_bgn_member && (
          <div className="absolute top-2 right-2 bg-black text-white px-2 py-0.5 text-xs font-medium uppercase tracking-wide">
            BGN
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-center space-x-3 mb-2">
          {safeAvatar && (
            <img src={safeAvatar} alt={streamer.display_name} className="w-8 h-8 rounded-full flex-shrink-0" />
          )}
          <div className="min-w-0">
            <h4 className="text-black font-medium text-sm truncate">{streamer.display_name || streamer.login}</h4>
            <p className="text-gray-400 text-xs truncate">@{streamer.login}</p>
          </div>
        </div>

        {streamer.game_name && (
          <p className="text-gray-600 text-xs truncate mb-3">{streamer.game_name}</p>
        )}
        {streamer.is_live && streamer.stream_title && (
          <p className="text-gray-500 text-xs truncate mb-3 italic">{streamer.stream_title}</p>
        )}

        {!streamer.is_bgn_member && (
          <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
            <Tv size={11} />
            <span>View on Twitch</span>
          </div>
        )}
      </div>
    </>
  );

  if (streamer.is_bgn_member) {
    return <Link to={profileUrl} className={cardClass}>{inner}</Link>;
  }

  return (
    <a href={twitchUrl} target="_blank" rel="noopener noreferrer" className={cardClass}>
      {inner}
    </a>
  );
};


export default StreamersPage;